import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import { useCoupons } from "@/Shared/Coupons/Hooks/useCoupons";
import { useDeleteQuotedShipmentDialog } from "@/Features/ViewShipments/Exports/Dialogs/ViewShipmentsDeleteQuotedShipmentDialog";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import { groupBy } from "@/Helpers/groupBy";
import { useOnce } from "@/Hooks/useOnce";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShipmentService } from "@/Services/ShipmentService";
import ShipmentRow from "@/Shared/Shipment/CustomComponents/ShipmentRow";
import { DashboardQuoteScreenGroupHeader } from "./Headers/DashboardQuoteScreenGroupHeader";
import { dashboardGenerateQuoteScreenShipmentGroupKey } from "./Helpers/dashboardGenerateQuoteScreenShipmentGroupKey";
import { BranchFilterOptionType } from "@/Shared/Types/BranchFilterOptionType";
import {
  UserSelectType,
  useDashboardUserSelect,
} from "@/Features/Dashboard/Exports/Hooks/useDashboardUserSelect";

interface DashboardRowForQuotedShipmentProps {
  shipment: PreBookingShipment;
  onClick: () => void;
  onDelete: () => Promise<void>;
}

function DashboardRowForQuotedShipment_(
  props: DashboardRowForQuotedShipmentProps,
) {
  const { shipment, onClick, onDelete } = props;
  const { discountQuote } = useCoupons();

  let carrierIdentifier = undefined;

  if (shipment.selectedQuote) {
    carrierIdentifier = shipment.selectedQuote.carrierIdentifier;
  }

  function discount(q: Quote | undefined) {
    if (q === undefined) return undefined;

    return discountQuote(q);
  }

  return (
    <ShipmentRow
      key={shipment.shipmentId}
      lineItems={shipment.lineItems}
      shipmentState={ShipmentState.Quoted}
      pickupLocation={shipment.pickupLocation}
      deliveryLocation={shipment.deliveryLocation}
      pickupDate={shipment.pickupDate}
      pickupHours={shipment.pickupLocation.hours}
      appointmentDate={undefined}
      expectedDeliveryDate={shipment.selectedQuote?.expectedDeliveryDate}
      expectedDeliveryHours={undefined}
      actualDeliveryDate={undefined}
      actualDeliveryTime={undefined}
      carrierIdentifier={carrierIdentifier}
      selectedQuote={discount(shipment.selectedQuote)}
      cheapestQuote={discount(shipment.cheapestQuote)}
      onClick={onClick}
      pickupReference={shipment.pickupReferenceNumber || ""}
      deliveryReference={shipment.deliveryReferenceNumber || ""}
      onDelete={onDelete}
      manualQuotingOpen={shipment.manualQuotingOpen}
    />
  );
}

interface SectionForQuotedShipmentStateProps {
  shipments: Array<PreBookingShipment>;
  reloadShipments: () => Promise<void>;
}

function SectionForQuotedShipmentState_(
  props: SectionForQuotedShipmentStateProps,
) {
  const navigate = useNavigate();
  const deleteQuotedShipmentDialog = useDeleteQuotedShipmentDialog();
  const shipmentsService = useShipmentService();
  const { shipments } = props;

  function onClick(shipment: PreBookingShipment) {
    let url;

    if (shipment.isReviewingPriorToBookingShipment) {
      url = `/book/review?shipmentId=${shipment.shipmentId}`;
    } else {
      if (shipment.selectedQuote) {
        url = `/book/pickup-address?shipmentId=${shipment.shipmentId}`;
      } else {
        url = `/book/quotes?shipmentId=${shipment.shipmentId}`;
      }
    }

    navigate(url);
  }

  async function onDelete(shipment: PreBookingShipment) {
    const reason = await deleteQuotedShipmentDialog.show(
      shipment.cheapestQuote,
    );

    if (reason !== undefined) {
      await shipmentsService.deleteQuotedShipment(shipment.shipmentId, reason);
      await props.reloadShipments();
    }
  }

  if (shipments.length === 0) {
    return <></>;
  }

  const grouped = groupBy(
    shipments,
    dashboardGenerateQuoteScreenShipmentGroupKey,
  );

  return (
    <>
      {deleteQuotedShipmentDialog.dialogs}
      {/* The group header used to carry its own mt-6/mb-2; the rhythm is the
          list's business, so it lives here now. The rows themselves butt
          together, hence the inner gap of 0. */}
      <Stack gap={6}>
        {grouped.map(function (o) {
          // The group key is the lane itself, so the React key is that lane
          // spelled out — the previous fragment had no key at all.
          const lane = `${o.key.pickupCity}, ${o.key.pickupStateOrProvinceCode} → ${o.key.deliveryCity}, ${o.key.deliveryStateOrProvinceCode}`;
          return (
            <Stack key={lane} gap={2}>
              <DashboardQuoteScreenGroupHeader group={o.key} />
              <Stack gap={0}>
                {o.value.map(function (s) {
                  return (
                    <DashboardRowForQuotedShipment_
                      key={s.shipmentId}
                      shipment={s}
                      onClick={function () {
                        onClick(s);
                      }}
                      onDelete={async function () {
                        await onDelete(s);
                      }}
                    />
                  );
                })}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </>
  );
}

function AddShipmentButton_() {
  const navigate = useNavigate();

  function onClick() {
    navigate("/book/details");
  }

  return (
    <Button primary onClick={onClick}>
      Get Instant Quotes
    </Button>
  );
}

const QuotesScreen: React.FC = () => {
  const shipmentsService = useShipmentService();
  const [quotedShipments, setQuotedShipments] = useState<
    Array<PreBookingShipment>
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const {
    element: UserSelectElement,
    filterByUserId,
    filterByBranchId,
  } = useDashboardUserSelect(UserSelectType.Quoting);

  async function loadShipments() {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await shipmentsService.getShipments();
      setQuotedShipments(response.quoted);
    } catch (e) {
      console.warn("Error loading quotes:", { e });
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }

  useOnce(async () => {
    await loadShipments();
  });

  if (loading) {
    return <PageLoadingSpinner />;
  }

  const filteredShipments = quotedShipments
    .filter(function (shipment) {
      if (shipment.quotedBy === undefined) {
        return true;
      }

      if (filterByUserId === undefined || filterByUserId === "") {
        return true;
      }

      return shipment.quotedBy === filterByUserId;
    })
    .filter(function (shipment) {
      if (filterByBranchId === undefined || filterByBranchId === "") {
        return true;
      }

      if (filterByBranchId === BranchFilterOptionType.Uncoded) {
        return shipment.branchId === undefined;
      }

      return shipment.branchId === filterByBranchId;
    });

  const noShipments = quotedShipments.length === 0;

  const showUncodedOption = quotedShipments.some(
    (s) => s.branchId === undefined,
  );

  return (
    <>
      <FullWidthLayout
        header={<>Quotes</>}
        microcopy={
          noShipments ? "" : <>These are the recent quotes you have run</>
        }
        loading={loading}
        showEmptyState={noShipments}
        onRetryFailedLoad={loadFailed ? loadShipments : undefined}
        emptyState={
          <Stack gap={6} align="center">
            <Stack gap={2} align="center">
              <Heading level={1}>You don&apos;t have any recent quotes</Heading>
              <T color="secondary">
                Get quotes to start booking your next shipment.
              </T>
            </Stack>
            <AddShipmentButton_ />
          </Stack>
        }
        rightContent={UserSelectElement(showUncodedOption)}
        content={
          <>
            <SectionForQuotedShipmentState_
              shipments={filteredShipments}
              reloadShipments={loadShipments}
            />
          </>
        }
      />
    </>
  );
};

export default QuotesScreen;
